import { getApps, initializeApp } from 'firebase/app';
import {
  ReCaptchaV3Provider,
  initializeAppCheck,
} from 'firebase/app-check';
import {
  connectAuthEmulator,
  getAuth,
  setPersistence,
  browserLocalPersistence,
} from 'firebase/auth';
import {
  connectFirestoreEmulator,
  getFirestore,
} from 'firebase/firestore';
import {
  connectFunctionsEmulator,
  getFunctions,
} from 'firebase/functions';
import { getAnalytics, isSupported } from 'firebase/analytics';

const config = {
  apiKey: import.meta.env.VITE_REACT_APP_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: import.meta.env.VITE_REACT_APP_FIREBASE_DATABASE_URL,
  projectId: import.meta.env.VITE_REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_REACT_APP_FIREBASE_APP_ID,
  ...(import.meta.env.VITE_REACT_APP_MEASUREMENT_ID
    ? { measurementId: import.meta.env.VITE_REACT_APP_MEASUREMENT_ID }
    : {}),
};

export const firebaseConfigured = Boolean(
  config.apiKey && config.projectId && config.appId && config.authDomain,
);

export const firebaseApp = firebaseConfigured
  ? (getApps()[0] || initializeApp(config))
  : null;

export const auth = firebaseApp ? getAuth(firebaseApp) : null;
export const db = firebaseApp ? getFirestore(firebaseApp) : null;
export const functions = firebaseApp
  ? getFunctions(
      firebaseApp,
      import.meta.env.VITE_REACT_APP_FUNCTIONS_REGION || 'us-central1',
    )
  : null;

if (auth) {
  setPersistence(auth, browserLocalPersistence).catch((error) => {
    console.warn('Could not enable Firebase Auth persistence:', error);
  });
}

const isBrowser = typeof window !== 'undefined';

const hostname = isBrowser ? window.location.hostname : '';
const isLocalHost = ['localhost', '127.0.0.1', '::1'].includes(hostname);

const environment = String(
  import.meta.env.VITE_REACT_APP_ENVIRONMENT || '',
).toLowerCase();

const projectId = String(config.projectId || '').toLowerCase();

const emulatorSetting = String(
  import.meta.env.VITE_REACT_APP_USE_EMULATORS || 'auto',
).toLowerCase();

const explicitlyEnabled = ['true', '1', 'yes', 'on'].includes(emulatorSetting);
const explicitlyDisabled = ['false', '0', 'no', 'off'].includes(emulatorSetting);
const autoMode = emulatorSetting === 'auto' || emulatorSetting === '';

const isDevProject = projectId === 'ooo-maker-dev';

const isDevEnvironment = [
  'local',
  'dev',
  'development',
  'dev-emulator',
].includes(environment);

// Safety rule:
// Never connect emulators unless the app is running in browser on localhost
// and the configured Firebase project is the dev project.
const useEmulators =
  Boolean(auth && db && functions) &&
  isBrowser &&
  isLocalHost &&
  isDevProject &&
  !explicitlyDisabled &&
  (explicitlyEnabled || (autoMode && isDevEnvironment));

if (useEmulators && !window.__NUDGE_EMULATORS_CONNECTED__) {
  connectFunctionsEmulator(functions, 'localhost', 5001);
  connectFirestoreEmulator(db, 'localhost', 8080);
  connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
  window.__NUDGE_EMULATORS_CONNECTED__ = true;

  console.log('[Nudge] Connected to Firebase emulators', {
    environment,
    projectId,
    hostname,
  });
} else if (isBrowser) {
  console.log('[Nudge] Firebase live mode', {
    environment,
    projectId,
    hostname,
    emulatorSetting,
    reason: !isLocalHost
      ? 'not-localhost'
      : !isDevProject
        ? 'not-dev-project'
        : explicitlyDisabled
          ? 'disabled'
          : 'not-enabled',
  });
}

const appCheckEnabled =
  String(import.meta.env.VITE_REACT_APP_ENABLE_APP_CHECK || 'false') === 'true';

if (
  firebaseApp &&
  appCheckEnabled &&
  import.meta.env.VITE_REACT_APP_RECAPTCHA_KEY &&
  isBrowser &&
  !window.__NUDGE_APP_CHECK_INITIALIZED__
) {
  initializeAppCheck(firebaseApp, {
    provider: new ReCaptchaV3Provider(
      import.meta.env.VITE_REACT_APP_RECAPTCHA_KEY,
    ),
    isTokenAutoRefreshEnabled: true,
  });
  window.__NUDGE_APP_CHECK_INITIALIZED__ = true;
}

export let analytics = null;
if (firebaseApp && import.meta.env.VITE_REACT_APP_MEASUREMENT_ID) {
  isSupported()
    .then((supported) => {
      if (supported) analytics = getAnalytics(firebaseApp);
    })
    .catch(() => {});
}

export default firebaseApp;